import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Box,
  SegmentedControl,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { cohortsApi, type UpdateSessionConfigRequest } from '../../../services/api/cohorts';
import type { CohortWithDetails, SessionFrequency, PreferredDay, PreferredTime } from '../../../types';
import {
  SESSION_COUNT_OPTIONS,
  FREQUENCY_OPTIONS,
  DAY_OPTIONS,
  TIME_OPTIONS_WITH_TIMES,
} from '../../../config/cohortConfig';
import classes from './EditModals.module.css';

interface EditSessionConfigModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
}

export function EditSessionConfigModal({
  opened,
  onClose,
  cohort,
  onUpdate,
}: EditSessionConfigModalProps) {
  const [saving, setSaving] = useState(false);
  const [sessionCount, setSessionCount] = useState(String(cohort.session_count || 6));
  const [frequency, setFrequency] = useState<SessionFrequency>(cohort.session_frequency || 'biweekly');
  const [preferredDays, setPreferredDays] = useState<PreferredDay[]>(cohort.preferred_days || []);
  const [preferredTimes, setPreferredTimes] = useState<PreferredTime[]>(cohort.preferred_times || []);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      setSessionCount(String(cohort.session_count || 6));
      setFrequency(cohort.session_frequency || 'biweekly');
      setPreferredDays(cohort.preferred_days || []);
      setPreferredTimes(cohort.preferred_times || []);
    }
  }, [opened, cohort]);

  const toggleDay = (day: PreferredDay) => {
    setPreferredDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleTime = (time: PreferredTime) => {
    setPreferredTimes(prev =>
      prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data: UpdateSessionConfigRequest = {
        session_count: parseInt(sessionCount, 10),
        session_frequency: frequency,
        preferred_days: preferredDays,
        preferred_times: preferredTimes,
      };
      const updated = await cohortsApi.updateSessionConfig(cohort.id, data);
      onUpdate(updated);
      onClose();
      notifications.show({
        title: 'Success',
        message: 'Session configuration updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update configuration',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Session Configuration"
      size="md"
    >
      <Stack gap="lg">
        <Box>
          <Text size="sm" fw={500} mb="xs">
            Number of Sessions
          </Text>
          <SegmentedControl
            data={[...SESSION_COUNT_OPTIONS]}
            value={sessionCount}
            onChange={setSessionCount}
            fullWidth
          />
        </Box>

        <Box>
          <Text size="sm" fw={500} mb="xs">
            Session Frequency
          </Text>
          <SegmentedControl
            data={[...FREQUENCY_OPTIONS]}
            value={frequency}
            onChange={(val) => setFrequency(val as SessionFrequency)}
            fullWidth
          />
        </Box>

        <Box>
          <Text size="sm" fw={500} mb="xs">
            Preferred Days
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Select which days work best for scheduling sessions
          </Text>
          <Group gap="xs">
            {DAY_OPTIONS.map(({ value, label }) => (
              <UnstyledButton
                key={value}
                onClick={() => toggleDay(value)}
                className={classes.dayButton}
                data-active={preferredDays.includes(value)}
              >
                {label}
              </UnstyledButton>
            ))}
          </Group>
        </Box>

        <Box>
          <Text size="sm" fw={500} mb="xs">
            Preferred Times
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Select preferred time slots for sessions
          </Text>
          <Group gap="xs">
            {TIME_OPTIONS_WITH_TIMES.map(({ value, label }) => (
              <UnstyledButton
                key={value}
                onClick={() => toggleTime(value)}
                className={classes.timeButton}
                data-active={preferredTimes.includes(value)}
              >
                {label}
              </UnstyledButton>
            ))}
          </Group>
        </Box>

        <Group justify="flex-end" className="modal-footer">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditSessionConfigModal;
